import { LogOut, Settings } from 'lucide-react';
import { useMsal } from '@azure/msal-react';
import { useUser } from '@/contexts/UserContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { logger } from '@/services/logger';
import { env } from '@/env';

/**
 * User menu component for sidebar footer
 * Displays current user and allows sign out
 */
export function UserMenu() {
  const { instance } = useMsal();
  const { account, userProfile, clearUserProfile } = useUser();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [tokenClaims, setTokenClaims] = useState<Record<string, unknown> | null>(null);

  /**
   * Fetch the ID token to get claims information including given_name and family_name
   */
  useEffect(() => {
    const getTokenClaims = async () => {
      try {
        // Try to get the ID token from the cache
        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
          const currentAccount = accounts[0];

          // Try to get a fresh token with the login scopes
          const response = await instance.acquireTokenSilent({
            scopes: [`api://${env.VITE_DATA_MISSION_CLIENT_ID}/access_as_user`],
            account: currentAccount,
          });

          setTokenClaims(response.idTokenClaims as Record<string, unknown>);
        }
      } catch (error) {
        console.error('Failed to get token claims:', error);
      }
    };

    void getTokenClaims();
  }, [instance, account]);

  /**
   * Handle user sign out
   */
  const handleSignOut = async () => {
    try {
      // Clear cached profile on logout
      clearUserProfile();

      logger.trackEvent('UserSession.SignOut', {
        accountId: account.homeAccountId,
        username: account.username,
      });
      await instance.logoutRedirect({
        account: account,
      });
    } catch (error) {
      logger.error(error instanceof Error ? error : new Error('Sign out failed'), {
        context: 'UserMenu.handleSignOut',
      });
    }
  };

  /**
   * Get first name from cached profile, token claims, or account name
   */
  const getFirstName = () => {
    // First priority: cached profile from UserContext (optimistic update)
    if (userProfile?.givenName) {
      return userProfile.givenName;
    }

    // Second priority: token claims
    if (tokenClaims?.given_name) {
      return tokenClaims.given_name as string;
    }

    // Fall back to parsing the name field
    if (tokenClaims?.name) {
      const name = tokenClaims.name as string;
      return name.split(' ')[0];
    }

    if (account.name) {
      return account.name.split(' ')[0];
    }

    return account.username.split('@')[0];
  };

  /**
   * Get last name from cached profile, token claims, or account name
   */
  const getLastName = () => {
    // First priority: cached profile from UserContext (optimistic update)
    if (userProfile?.surname) {
      return userProfile.surname;
    }

    // Second priority: token claims
    if (tokenClaims?.family_name) {
      return tokenClaims.family_name as string;
    }

    // Fall back to parsing the name field
    if (tokenClaims?.name) {
      const name = tokenClaims.name as string;
      const parts = name.split(' ');
      return parts.length > 1 ? parts[parts.length - 1] : '';
    }

    if (account.name) {
      const parts = account.name.split(' ');
      return parts.length > 1 ? parts[parts.length - 1] : '';
    }

    return '';
  };

  /**
   * Get full name for display
   */
  const getFullName = () => {
    const firstName = getFirstName();
    const lastName = getLastName();
    return lastName ? `${firstName} ${lastName}` : firstName;
  };

  /**
   * Get user initials for avatar
   */
  const getInitials = () => {
    const firstName = getFirstName();
    const lastName = getLastName();

    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }

    if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    }

    return 'U';
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <span className="text-xs font-medium">{getInitials()}</span>
              </div>
              <div className="flex flex-col gap-0.5 leading-none min-w-0">
                <span className="font-medium truncate">{getFullName()}</span>
                <span className="text-xs text-muted-foreground truncate">{account.username}</span>
              </div>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]" align="start">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{getFullName()}</p>
                <p className="text-xs leading-none text-muted-foreground">{account.username}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => { void navigate('/dashboard/settings'); }}>
              <Settings className="mr-2 size-4" />
              {t.sidebar.userMenu.settings}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => { void handleSignOut(); }}>
              <LogOut className="mr-2 size-4" />
              {t.sidebar.userMenu.signOut}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
